import React, { useEffect, useRef, useState } from "react";
import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { closeModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";

import {
  getBrandCoreItem,
  handleClearDraftItem,
  handleUpdateDraftItem as onUpdateDraftItem,
  updateBrandCoreItem,
  createBrandCoreItem,
  UpdateFormDataPayload,
} from "store/cms/footer/brandCore/slice";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import styles from "../brandCore/CmsFooterBrandCoreEditModal.module.scss";

import BrandCoreMainTab from "./tabs/BrandCoreMainTab";
import BrandCoreTrailTab from "./tabs/BrandCoreTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

enum ModalBrandCoreTabs {
  main = "main",
  trail = "trail",
}

const CmsFooterBrandCoreEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const logoRef = useRef<File | null>(null);

  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  const geoList = useAppSelector(selectFilteredGeo);
  const isItemFetched = useAppSelector(
    (store) => store["cms-brandCore"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-brandCore"].isItemFetching,
  );
  const draftItem = useAppSelector((store) => store["cms-brandCore"].draftItem);

  const [activeTab, setActiveTab] = useState<ModalBrandCoreTabs>(
    ModalBrandCoreTabs.main,
  );

  const permissions = usePermissionHelper();

  const tabs = [ModalBrandCoreTabs.main, ModalBrandCoreTabs.trail];

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getBrandCoreItem(+entityId));
    }
  }, [entityId, isItemFetched]);

  const isLoaded = () => {
    if (entityId && !isItemFetched) return false;
    return true;
  };

  const loaded = isLoaded();

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const canBeSaved = () => {
    if (!draftItem.title) return false;
    return draftItem.isModified || false;
  };

  const handleSave = async () => {
    const { id, title, isActive, geo, columns } = draftItem;

    const data: UpdateFormDataPayload = {
      id,
      title,
      isActive,
      geo: geo || geoList[0],
      columns,
      ...(logoRef.current && { logo: logoRef.current }),
    };

    formErrors.resetErrors();

    try {
      if (!id) {
        const { id: _, ...restData } = data;

        const result = await dispatch(createBrandCoreItem(restData)).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.brandCore}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        await dispatch(updateBrandCoreItem(data)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };
  const handleTabClick = (tab: ModalBrandCoreTabs) => {
    if (!isEdit && tab !== ModalBrandCoreTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalBrandCoreTabs.main) {
      return (
        <BrandCoreMainTab
          onChangeLogo={(file) => {
            logoRef.current = file;
          }}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }
    if (activeTab === ModalBrandCoreTabs.trail) {
      return <BrandCoreTrailTab entityId={entityId!} />;
    }
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("brandCore")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content className={styles.cmsFooterContentWrapper}>
            {renderTab()}
          </Modal.Content>

          {activeTab !== ModalBrandCoreTabs.trail && permissions?.update && (
            <Modal.Footer>
            <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
            <Button
              isPrimary
              handleClick={handleSave}
              txt={t("save")}
              isLoad={isItemFetching}
              disabled={!canBeSaved()}
            />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsFooterBrandCoreEditModal;
